import {
  AgGridHamburgerMenu,
  Box,
  Button,
  ButtonVariants,
  type CareOrder,
  type ColumnDef,
  FormControlSizes,
  HStack,
  type Quote,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { CARE_ORDER_CUSTOM_COLUMN_IDS } from './careOrderColumnIds';
import { type CareOrderBlotterEntity, isCareOrderRow, isQuoteRow } from './types';

export interface UseCareOrderBlotterActionColumnParams {
  onSendRFQ: (careOrder: CareOrder) => void;
  onShowRFQ: (quote: Quote) => void;
  onShowTimeline: (entity: CareOrderBlotterEntity) => void;
}

export const useCareOrderBlotterActionColumn = ({
  onSendRFQ,
  onShowRFQ,
  onShowTimeline,
}: UseCareOrderBlotterActionColumnParams) => {
  const column: ColumnDef<CareOrderBlotterEntity> = useMemo(() => {
    return {
      type: 'custom',
      id: CARE_ORDER_CUSTOM_COLUMN_IDS.Action,
      title: 'Actions',
      exportable: false, // make sure the column doesn't show up in exports
      width: 95,
      maxWidth: 95,
      minWidth: 95,

      params: {
        suppressColumnsToolPanel: true, // dont render as a selectable item in the column chooser
        suppressAutoSize: true,
        sortable: false,
        resizable: false,
        pinned: 'right',
        lockVisible: true,
        lockPosition: true,
        lockPinned: true,
        // The entire data object is our value in this column. We want to re-render on every data update.
        valueGetter: (params: ValueGetterParams<CareOrderBlotterEntity>): CareOrderBlotterEntity | undefined => {
          return params.data;
        },
        cellRenderer: (params: ICellRendererParams<CareOrderBlotterEntity, CareOrderBlotterEntity>) => {
          const entity = params.data;
          if (!entity) {
            return null;
          }

          const callbacks = { onShowRFQ, onSendRFQ, onShowTimeline };
          const actionItems = getCareOrderBlotterActions(entity);
          const columnItem = actionItems.find(item => item.renderIn === 'column' && !item.hidden);
          const menuItems = actionItems.filter(item => item.renderIn === 'menu' && !item.hidden);

          const renderMenuItems = () => {
            return menuItems.map(item => (
              <Button
                key={item.type}
                disabled={item.disabled}
                onClick={() => {
                  item.onClick(callbacks);
                }}
                size={FormControlSizes.Small}
                flex="1"
                data-testid={item.testid}
              >
                {item.label}
              </Button>
            ));
          };

          return (
            <HStack gap="spacing6" w="100%" justifyContent="flex-start">
              <Box flex="1">
                {columnItem && (
                  <Button
                    width="100%"
                    key={columnItem.type}
                    disabled={columnItem.disabled}
                    onClick={() => columnItem.onClick(callbacks)}
                    variant={columnItem.variant}
                    size={FormControlSizes.Small}
                    flex="1"
                    data-testid={columnItem.testid}
                  >
                    {columnItem.label}
                  </Button>
                )}
              </Box>

              <AgGridHamburgerMenu
                renderItems={renderMenuItems}
                {...params}
                iconButtonProps={{
                  size: FormControlSizes.Small,
                  ghost: false,
                  disabled: menuItems.length === 0,
                }}
              />
            </HStack>
          );
        },
      } satisfies ColDef,
    };
  }, [onShowRFQ, onSendRFQ, onShowTimeline]);

  return column;
};

interface CareOrderBlotterAction {
  type: 'showRFQ' | 'sendRFQ' | 'details';
  /** Disabled state for the action button, both in the blotter column and in the kebab menu */
  disabled: boolean;
  /** Whether or not to hide the button */
  hidden: boolean;
  /** Decide if you want to render the action button in the column itself or in the kebab menu popover */
  renderIn: 'column' | 'menu';
  label: string;
  variant?: ButtonVariants;
  testid?: string;
  onClick: (params: UseCareOrderBlotterActionColumnParams) => void;
}

export function getCareOrderBlotterActions(entity: CareOrderBlotterEntity): CareOrderBlotterAction[] {
  if (isCareOrderRow(entity)) {
    return [
      {
        type: 'sendRFQ',
        disabled: !entity.isTradable,
        hidden: entity.isComplete,
        label: 'RFQ',
        renderIn: 'column',
        variant: ButtonVariants.Primary,
        testid: 'care-order-blotter-action-button-send-rfq',
        onClick: params => params.onSendRFQ(entity),
      },
      {
        type: 'details',
        disabled: false,
        hidden: false,
        label: 'Details',
        variant: ButtonVariants.Default,
        renderIn: 'menu',
        onClick: params => params.onShowTimeline(entity),
      },
    ];
  }

  if (isQuoteRow(entity)) {
    return [
      {
        type: 'showRFQ',
        disabled: !entity.canOpenQuote,
        hidden: entity.isTerminal,
        label: 'Show',
        renderIn: 'column',
        variant: ButtonVariants.Default,
        testid: 'care-order-blotter-action-button-show-rfq',
        onClick: params => params.onShowRFQ(entity),
      },
      {
        type: 'details',
        disabled: false,
        hidden: false,
        label: 'Details',
        variant: ButtonVariants.Default,
        renderIn: 'menu',
        onClick: params => params.onShowTimeline(entity),
      },
    ];
  }

  return [];
}
